import { Component, OnInit, Input, OnChanges, SimpleChanges, ElementRef, HostListener, forwardRef, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  setYear,
  eachDay,
  getDate,
  getMonth,
  getYear,
  isToday,
  isSameDay,
  isSameMonth,
  isSameYear,
  format,
  getDay,
  subDays,
  setDay,
  setMonth
} from 'date-fns';
import * as itLocale from 'date-fns/locale/it';
import { ISlimScrollOptions, SlimScrollEvent } from 'ngx-slimscroll';

export type AddClass = string | string[] | { [k: string]: boolean } | null;

export interface DatepickerOptions {
  minYear?: number; // default: anno corrente - 8
  maxYear?: number; // default: anno corrente + 7
  yearStep?: number; // default: 15;
  displayFormat?: string; // default: 'D/MM/YYYY'
  barTitleYearFormat?: string; // default: 'YYYY'
  barTitleMonthFormat?: string; // default: 'MMMM'
  dayNamesFormat?: string; // default 'ddd'
  barTitleIfEmpty?: string;
  firstCalendarDay?: number; // 0 = Domenica, 1 = Lunedì (default), ..
  locale?: object; // localizzazione da date-fns/locale/..
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string; // placeholder per il campo di input
  addClass?: AddClass;
  addStyle?: { [k: string]: any } | null;
  fieldId?: string;
  useEmptyBarTitle?: boolean; // se false viene mostratto barTitleIfEmpty nel caso in cui la data e vuota, default = false
}

let counter = 0; // per calcolare l'id del campo

/**
 * serve per verificare se i campi sono vuoti
 * @param value
 */
const isNil = (value: Date | DatepickerOptions) => {
  return (typeof value === 'undefined') || (value === null);
};

@Component({
  selector: 'datepicker',
  templateUrl: 'datepicker.component.html',
  styleUrls: ['datepicker.component.sass'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatepickerComponent), multi: true }
  ]
})
export class DatepickerComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() options: DatepickerOptions;

  scrollEvents: EventEmitter<SlimScrollEvent>;

  /**
   * Disable datepicker's input
   */
  @Input() headless = false;

  /**
   * Set datepicker's visibility state
   */
  @Input() isOpened = false;

  /**
   * Datepicker dropdown position
   */
  @Input() position = 'bottom-right';

  private positions = ['bottom-left', 'bottom-right', 'top-left', 'top-right'];

  innerValue: Date;
  displayValue: string;
  displayFormat: string;
  date: Date;
  barTitleMonth: string; // ***
  barTitleMonthFormat: string;
  barTitleYear: string;
  barTitleYearFormat: string;
  barTitleIfEmpty: string;
  minYear: number;
  maxYear: number;
  firstCalendarDay: number;
  view: string;
  months: { month: number; monthName: string; isThisMonth: boolean }[];
  years: { year: number; isThisYear: boolean }[];
  dayNames: string[];
  dayNamesFormat: string;
  scrollOptions: ISlimScrollOptions;
  days: {
    date: Date;
    day: number;
    month: number;
    year: number;
    inThisMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isSelectable: boolean;
  }[];
  locale: object;
  placeholder: string;
  addClass: AddClass;
  addStyle: { [k: string]: any } | null;
  fieldId: string;
  useEmptyBarTitle: boolean;
  disabled: boolean;
  yearStep: number;

  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  get value(): Date {
    return this.innerValue;
  }

  set value(val: Date) {
    this.innerValue = val;
    this.onChangeCallback(this.innerValue);
  }

  constructor(private elementRef: ElementRef) {
    this.scrollOptions = {
      barBackground: '#DFE3E9',
      gridBackground: '#FFFFFF',
      barBorderRadius: '3',
      gridBorderRadius: '3',
      barWidth: '6',
      gridWidth: '6',
      barMargin: '0',
      gridMargin: '0',
      alwaysVisible: true
    };
  }

  ngOnInit() {
    this.scrollEvents = new EventEmitter<SlimScrollEvent>();
    this.view = 'days';
    this.date = new Date();
    this.setOptions();
    this.initDayNames();
    this.initYears();
    this.initMonths();

    // Verifica se il valore di 'position' è coretto
    if (this.positions.indexOf(this.position) === -1) {
      throw new TypeError(`datepicker: invalid position property value '${this.position}' (expected: ${this.positions.join(', ')})`);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('options' in changes) {
      this.setOptions();
      this.initDayNames();
      this.init();
      this.initYears();
      this.initMonths();
    }
  }

  get defaultFieldId(): string {
    const value = `datepicker-${counter++}`;
    Object.defineProperty(this, 'defaultFieldId', {value});

    return value;
  }

  setOptions(): void {
    const today = new Date();
    this.minYear = this.options && this.options.minYear || getYear(today) - 8;
    this.maxYear = this.options && this.options.maxYear || getYear(today) + 7;
    this.displayFormat = this.options && this.options.displayFormat || 'D/M/YYYY';
    this.barTitleYearFormat = this.options && this.options.barTitleYearFormat || 'YYYY';
    this.barTitleMonthFormat = this.options && this.options.barTitleMonthFormat || 'MMMM';
    this.dayNamesFormat = this.options && this.options.dayNamesFormat || 'ddd';
    this.barTitleIfEmpty = this.options && this.options.barTitleIfEmpty || 'Seleziona anno';
    this.firstCalendarDay = this.options && this.options.firstCalendarDay || 1;
    this.locale = this.options && { locale: this.options.locale } || { locale: itLocale } ;
    this.placeholder = this.options && this.options.placeholder || 'Seleziona data';
    this.addClass = this.options && this.options.addClass || {};
    this.addStyle = this.options && this.options.addStyle || {};
    this.fieldId = this.options && this.options.fieldId || this.defaultFieldId;
    this.useEmptyBarTitle = this.options && 'useEmptyBarTitle' in this.options ? this.options.useEmptyBarTitle : false;
    this.yearStep = this.options && this.options.yearStep || 15;
  }

  nextMonth(): void {
    this.date = addMonths(this.date, 1);
    this.init();
  }

  prevMonth(): void {
    this.date = subMonths(this.date, 1);
    this.init();
  }

  nextYears(): void {
    this.maxYear += this.yearStep;
    this.minYear += this.yearStep;
    this.initYears();
    this.scrollToTop();
  }

  pastYears(): void {
    this.maxYear -= this.yearStep;
    this.minYear -= this.yearStep;
    this.initYears();
    this.scrollToBottom();
  }

  scrollToTop() {
    let event = null;
    Promise.resolve()
    .then(() => this.timeout(100))
    .then(() => {
      event = new SlimScrollEvent({
        type: 'scrollToTop',
        duration: 100,
        easing: 'linear'
      });
      this.scrollEvents.emit(event);
    });
  }

  scrollToBottom() {
    let event = null;
    Promise.resolve()
    .then(() => this.timeout(100))
    .then(() => {
      event = new SlimScrollEvent({
        type: 'scrollToBottom',
        duration: 100,
        easing: 'linear'
      });
      this.scrollEvents.emit(event);
    });
  }

  timeout(ms: number): Promise<{}> {
    return new Promise(resolve => setTimeout(() => resolve(), ms));
  }

  setDate(i: number): void {
    this.date = this.days[i].date;
    this.value = this.date;
    this.init();
    this.close();
  }

  setYear(i: number): void {
    this.date = setYear(this.date, this.years[i].year);
    this.init();
    this.initYears();
    this.view = 'months';
  }

  setMonth(i: number): void {
    this.date = setMonth(this.date, this.months[i].month);
    this.init();
    this.initMonths();
    this.view = 'days';
  }

  /**
   * Checks if specified date is in range of min and max dates
   * @param date
   */
  private isDateSelectable(date: Date): boolean {
    if (isNil(this.options)) {
      return true;
    }

    const minDateSet = !isNil(this.options.minDate);
    const maxDateSet = !isNil(this.options.maxDate);
    const timestamp = date.valueOf();

    if (minDateSet && (timestamp < this.options.minDate.valueOf())) {
      return false;
    }

    if (maxDateSet && (timestamp > this.options.maxDate.valueOf())) {
      return false;
    }

    return true;
  }

  init(): void {
    const actualDate = this.date || new Date();
    const start = startOfMonth(actualDate);
    const end = endOfMonth(actualDate);

    this.days = eachDay(start, end).map(date => {
      return {
        date: date,
        day: getDate(date),
        month: getMonth(date),
        year: getYear(date),
        inThisMonth: true,
        isToday: isToday(date),
        isSelected: isSameDay(date, this.innerValue) && isSameMonth(date, this.innerValue) && isSameYear(date, this.innerValue),
        isSelectable: this.isDateSelectable(date)
      };
    });

    const tmp = getDay(start) - this.firstCalendarDay;
    const prevDays = tmp < 0 ? 7 - this.firstCalendarDay : tmp;

    for (let i = 1; i <= prevDays; i++) {
      const date = subDays(start, i);
      this.days.unshift({
        date: date,
        day: getDate(date),
        month: getMonth(date),
        year: getYear(date),
        inThisMonth: false,
        isToday: isToday(date),
        isSelected: isSameDay(date, this.innerValue) && isSameMonth(date, this.innerValue) && isSameYear(date, this.innerValue),
        isSelectable: this.isDateSelectable(date)
      });
    }

    if (this.innerValue) {
      this.displayValue = format(this.innerValue, this.displayFormat, this.locale);
      this.barTitleYear = format(start, this.barTitleYearFormat, this.locale);
      this.barTitleMonth = format(start, this.barTitleMonthFormat, this.locale);
    } else {
      this.displayValue = '';
      this.barTitleYear = this.useEmptyBarTitle ? this.barTitleIfEmpty : format(start, this.barTitleYearFormat, this.locale);
      this.barTitleMonth = this.useEmptyBarTitle ? this.barTitleIfEmpty : format(start, this.barTitleMonthFormat, this.locale);
    }
  }

  initYears(): void {
    const range = this.maxYear - this.minYear;
    this.years = Array.from(new Array(range), (x, i) => this.maxYear - i).map(year => {
      return { year: year, isThisYear: year === getYear(this.date) };
    });
  }

  initMonths(): void {
    this.months = Array.from(new Array(12), (x, i) => 11 - i).map(month => {
      return { 
        month: month, 
        monthName: format(setMonth(new Date(), month), this.barTitleMonthFormat, this.locale), 
        isThisMonth: month === getMonth(this.date) };
    });
  }

  initDayNames(): void {
    this.dayNames = [];
    const start = this.firstCalendarDay;
    for (let i = start; i <= 6 + start; i++) {
      const date = setDay(new Date(), i);
      this.dayNames.push(format(date, this.dayNamesFormat, this.locale));
    }
  }

  toggleViewYear(): void {
    this.view = this.view === 'days' || this.view === 'months' ? 'years' : 'days';
  }

  toggleViewMonth() {
    this.view = this.view === 'days' || this.view === 'years' ? 'months' : 'days';
  }

  toggle(): void {
    this.isOpened = !this.isOpened;

    if (!this.isOpened && this.view === 'years') {
      this.toggleViewYear();
    } else if (!this.isOpened && this.view === 'months') {
      this.toggleViewMonth();
    }
  }

  close(): void {
    this.isOpened = false;

    if (this.view === 'years') {
      this.toggleViewYear();
    } else if (!this.isOpened && this.view === 'months') {
      this.toggleViewMonth();
    }
  }

  reset(fireValueChangeEvent = false): void {
    this.date = null;
    this.innerValue = null;
    this.init();
    if (fireValueChangeEvent && this.onChangeCallback) {
      this.onChangeCallback(this.innerValue);
    }
  }

  writeValue(val: Date) {
    if (val) {
      this.date = val;
      this.innerValue = val;
      this.init();
      this.displayValue = format(this.innerValue, this.displayFormat, this.locale);
      this.barTitleYear = format(startOfMonth(val), this.barTitleYearFormat, this.locale);
      this.barTitleMonth = format(startOfMonth(val), this.barTitleMonthFormat, this.locale);
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  @HostListener('document:click', ['$event']) onBlur(e: MouseEvent) {
    if (!this.isOpened) {
      return;
    }

    const input = this.elementRef.nativeElement.querySelector('.datepicker-input');

    if (input == null) {
      return;
    }

    if (e.target === input || input.contains(<any>e.target)) {
      return;
    }

    const container = this.elementRef.nativeElement.querySelector('.datepicker-calendar-container');
    if (container && container !== e.target && !container.contains(<any>e.target) && !(<any>e.target).classList.contains('year-unit')) {
      this.close();
    }
  }
}



// WEBPACK FOOTER //
// /home/david/vscode-workspace/TOP-casa-nel-web/frontend/src/app/components/utilita/datepicker/datepicker.component.ts